/**
 * Customily Preview Handler
 * Handles modal preview functionality with Add to Cart and Close buttons
 */

(function() {
  'use strict';

  // Constants
  const CONFIG = {
    TEMPLATES: {
      NOTICE_HTML: `
        <p class="customily_modal_preview--title">Please check the preview carefully</p>
        <p class="customily_modal_preview--notice_content">
          <em>Make sure the below customization is correct to receive your item faster</em>
        </p>
      `
    },
    
    SELECTORS: {
      CANVAS_CONTAINER: '.canvas-container',
      ADD_TO_CART_BTN: '.product-form__submit.button',
      MODAL_MAIN: '.customily-modal-preview-only .main',
      CLOSE_BTN: '.customily-close-button',
      PREVIEW_BTN: '.btn-preview-add-to-cart'
    },
    
    CSS_CLASSES: {
      PREVIEW_BTN: 'btn-preview-add-to-cart',
      CLOSE_BTN: 'customily-close-btn button',
      BTN_WRAPPER: 'btn-wrapper',
      RUNNING: 'running',
      MODIFY_NOTICE: 'customily_modal_preview--modify_notice',
      PROCESSED: 'customily-processed'
    }
  };

  /**
   * Handle Add to Cart button click
   * @param {Event} event - Click event
   */
  function handleAddToCartClick(event) {
    try {
      event.stopPropagation();
      
      const previewBtn = document.querySelector(CONFIG.SELECTORS.PREVIEW_BTN);
      const addToCartBtn = document.querySelector(CONFIG.SELECTORS.ADD_TO_CART_BTN);
      const closeBtn = document.querySelector(CONFIG.SELECTORS.CLOSE_BTN);
      
      if (previewBtn) {
        previewBtn.classList.add(CONFIG.CSS_CLASSES.RUNNING);
      }
      
      if (addToCartBtn) {
        addToCartBtn.click();
        setTimeout(() => {
          closeBtn.click();
        }, 500);
      }
      
    } catch (error) {
      console.error('Error in handleAddToCartClick:', error);
    }
  }

  /**
   * Handle Close button click
   * @param {Event} event - Click event
   */
  function handleCloseClick(event) {
    try {
      event.stopPropagation();
      event.preventDefault();
      
      const closeBtn = document.querySelector(CONFIG.SELECTORS.CLOSE_BTN);
      if (closeBtn) {
        closeBtn.click();
      }
      
    } catch (error) {
      console.error('Error in handleCloseClick:', error);
    }
  }

  /**
   * Create button wrapper with Add to Cart and Close buttons
   * @param {Element} addToCartBtn - Original add to cart button
   * @returns {Element} Button wrapper element
   */
  function createButtonWrapper(addToCartBtn) {
    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.className = CONFIG.CSS_CLASSES.BTN_WRAPPER;
    
    // Setup Add to Cart button
    addToCartBtn.classList.add(CONFIG.CSS_CLASSES.PREVIEW_BTN);
    addToCartBtn.addEventListener('click', handleAddToCartClick);
    
    // Create Close button
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = 'Close';
    closeBtn.className = CONFIG.CSS_CLASSES.CLOSE_BTN;
    closeBtn.addEventListener('click', handleCloseClick);
    
    // Append buttons to wrapper
    wrapper.appendChild(addToCartBtn);
    wrapper.appendChild(closeBtn);
    
    return wrapper;
  }

  /**
   * Create notice element
   * @returns {Element} Notice element
   */
  function createNoticeElement() {
    const noticeEl = document.createElement('div');
    noticeEl.className = CONFIG.CSS_CLASSES.MODIFY_NOTICE;
    noticeEl.innerHTML = CONFIG.TEMPLATES.NOTICE_HTML;
    return noticeEl;
  }

  /**
   * Setup preview functionality for canvas container
   * @param {Element} canvasContainer - Canvas container element
   */
  function setupPreview(canvasContainer) {
    try {
      // Check if already processed
      if (canvasContainer.classList.contains(CONFIG.CSS_CLASSES.PROCESSED)) {
        return;
      }
      
      const addToCartBtn = document.querySelector(CONFIG.SELECTORS.ADD_TO_CART_BTN);
      const modalMain = document.querySelector(CONFIG.SELECTORS.MODAL_MAIN);
      
      // Validate required elements
      if (!addToCartBtn || !modalMain) {
        console.warn('Required elements not found for preview setup');
        return;
      }
      
      // Create and append button wrapper
      const buttonWrapper = createButtonWrapper(addToCartBtn);
      modalMain.appendChild(buttonWrapper);
      
      // Create and append notice
      const noticeElement = createNoticeElement();
      modalMain.appendChild(noticeElement);
      
      // Mark as processed
      canvasContainer.classList.add(CONFIG.CSS_CLASSES.PROCESSED);
      
    } catch (error) {
      console.error('Error in setupPreview:', error);
    }
  }

  /**
   * Process newly added DOM node
   * @param {Node} node - Added DOM node
   */
  function processAddedNode(node) {
    try {
      // Skip non-element nodes
      if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      
      // Check if node is canvas container
      if (node.classList?.contains('canvas-container')) {
        setupPreview(node);
        return;
      }
      
      // Check for canvas containers within the node
      const canvasContainers = node.querySelectorAll?.(CONFIG.SELECTORS.CANVAS_CONTAINER);
      if (canvasContainers?.length > 0) {
        canvasContainers.forEach(setupPreview);
      }
      
    } catch (error) {
      console.error('Error processing added node:', error);
    }
  }

  /**
   * Initialize the functionality
   */
  function init() {
    try {
      // Setup existing canvas containers
      const existingContainers = document.querySelectorAll(CONFIG.SELECTORS.CANVAS_CONTAINER);
      existingContainers.forEach(setupPreview);
      
      // Setup mutation observer for dynamic content
      const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(processAddedNode);
          }
        });
      });
      
      // Start observing
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: false,
        characterData: false
      });
      
      console.log('Customily preview initialized successfully');
      
    } catch (error) {
      console.error('Error initializing Customily preview:', error);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/**
 * Sticky Preview Button - Optimized
 * Creates mobile sticky button that triggers #customily-preview-button
 */

(function() {
  'use strict';

  const CONFIG = {
    TARGET_ID: 'customily-preview-button',
    STICKY_ID: 'sticky-preview-btn',
    CLASSES: {
      HIDDEN: 'hidden',
      VISIBLE: 'visible'
    },
    TIMEOUTS: {
      ANIMATION: 300,
      OBSERVER_CLEANUP: 60000
    }
  };

  let stickyButton = null;
  let intersectionObserver = null;
  let mutationObserver = null;
  let isVisible = false;

  // Mobile detection
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  function createButton() {
    if (stickyButton) return;

    // Create wrapper div
    const wrapper = document.createElement('div');
    wrapper.id = `${CONFIG.STICKY_ID}-wrapper`;
    wrapper.className = `${CONFIG.STICKY_ID}-wrapper ${CONFIG.CLASSES.HIDDEN}`;

    // Create button
    stickyButton = document.createElement('button');
    stickyButton.id = CONFIG.STICKY_ID;
    stickyButton.className = CONFIG.STICKY_ID;
    stickyButton.textContent = 'Preview Your Personalization';
    stickyButton.onclick = triggerOriginal;

    wrapper.appendChild(stickyButton);
    document.body.appendChild(wrapper);

    // Update references to use wrapper
    stickyButton = wrapper;
  }

  function triggerOriginal() {
    const target = document.getElementById(CONFIG.TARGET_ID);
    target?.click();
  }

  function showButton() {
    if (isVisible || !stickyButton) return;
    isVisible = true;
    stickyButton.classList.remove(CONFIG.CLASSES.HIDDEN);
    requestAnimationFrame(() => stickyButton.classList.add(CONFIG.CLASSES.VISIBLE));
  }

  function hideButton() {
    if (!isVisible || !stickyButton) return;
    isVisible = false;
    stickyButton.classList.remove(CONFIG.CLASSES.VISIBLE);
    setTimeout(() => stickyButton.classList.add(CONFIG.CLASSES.HIDDEN), CONFIG.TIMEOUTS.ANIMATION);
  }

  function startTracking() {
    const target = document.getElementById(CONFIG.TARGET_ID);
    if (!target || intersectionObserver) return;

    intersectionObserver = new IntersectionObserver(([entry]) => {
      entry.isIntersecting ? hideButton() : showButton();
    }, { threshold: 0 });

    intersectionObserver.observe(target);
  }

  function watchForTarget() {
    if (document.getElementById(CONFIG.TARGET_ID)) {
      startTracking();
      return;
    }

    mutationObserver = new MutationObserver(mutations => {
      let targetFound = false;
      let targetRemoved = false;

      mutations.forEach(mutation => {
        // Check for added nodes
        Array.from(mutation.addedNodes).forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE && 
              (node.id === CONFIG.TARGET_ID || node.querySelector?.(`#${CONFIG.TARGET_ID}`))) {
            targetFound = true;
          }
        });

        // Check for removed nodes
        Array.from(mutation.removedNodes).forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE && 
              (node.id === CONFIG.TARGET_ID || node.querySelector?.(`#${CONFIG.TARGET_ID}`))) {
            targetRemoved = true;
          }
        });
      });

      if (targetFound) {
        startTracking();
      }

      if (targetRemoved) {
        cleanup(intersectionObserver);
        intersectionObserver = null;
        hideButton();
      }
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    // Auto-cleanup if target never appears
    setTimeout(() => {
      if (!intersectionObserver) {
        console.log('Sticky preview: Target element not found, cleaning up');
        cleanup(mutationObserver);
        destroyButton();
      }
    }, CONFIG.TIMEOUTS.OBSERVER_CLEANUP);
  }

  function destroyButton() {
    if (stickyButton) {
      stickyButton.remove();
      stickyButton = null;
      isVisible = false;
    }
  }

  function cleanup(observer) {
    observer?.disconnect();
  }

  function injectCSS() {
    if (document.getElementById('sticky-preview-css')) return;

    const style = document.createElement('style');
    style.id = 'sticky-preview-css';
    style.textContent = `
      .${CONFIG.STICKY_ID}-wrapper {
        position: fixed;
        bottom: 0;
        left: 0;
        right:0;
        z-index: 1000;
        background: white;
        padding: 1rem;
        transition: all .3s ease;
        opacity: 0;
        pointer-events: none;
      }
      .${CONFIG.STICKY_ID}-wrapper.${CONFIG.CLASSES.VISIBLE} {
        opacity: 1;
        pointer-events: auto;
      }
      .${CONFIG.STICKY_ID} {
        border: none;
        border-radius: 10px;
        padding: 14px 24px;
        font-weight: 600;
        cursor: pointer;
        transition: all .2s ease;
        display: block;
        width: 100%;
        color: #fff;
        background-color: var(--color-primary);
        border-color: var(--color-primary);
        font-size: 16px;
        font-weight: 600;
        text-transform: uppercase;
      }
      @media (min-width: 990px) {
        .${CONFIG.STICKY_ID}-wrapper { display: none !important; }
      }
    `;

    document.head.appendChild(style);
  }

  function init() {
    if (!isMobile) return;
    
    createButton();
    watchForTarget();
    injectCSS();
  }

  // Initialize
  document.readyState === 'loading' 
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();